import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Heart, Mail, Lock, User, ArrowLeft, Sparkles, Eye, EyeOff } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAppMode } from "@/context/AppModeContext";
import { useLocale } from "@/i18n/LocaleContext";

const DLUXE_REGISTER_IMG = "https://static.prod-images.emergentagent.com/jobs/e1250066-ed24-4faf-8451-7a13993519fa/images/1f58023055c49d06a7482d240b315ea415afae2075ea340e6976433f37c2f38d.jpeg";

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { isDluxe, appName } = useAppMode();
  const { t, lang } = useLocale();
  const [loading, setLoading] = useState(false);
  const [giftInfo, setGiftInfo] = useState(null);
  const [saCode, setSaCode] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    gender: "",
    referral_code: "",
    gift_code: "",
    terms_accepted: false
  });

  useEffect(() => {
    const ref = searchParams.get("ref");
    const gift = searchParams.get("gift");
    const sa = searchParams.get("sa");
    if (sa) {
      localStorage.setItem("pending_sa_code", sa.toUpperCase());
      setSaCode(sa.toUpperCase());
    }
    if (ref || gift) {
      setFormData((prev) => ({
        ...prev,
        ...(ref ? { referral_code: ref.toUpperCase() } : {}),
        ...(gift ? { gift_code: gift.toUpperCase() } : {}),
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!formData.gift_code) return;
    api.get(`/vouchers/gift-info/${formData.gift_code}`)
      .then((r) => setGiftInfo(r.data))
      .catch(() => setGiftInfo(null));
  }, [formData.gift_code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gender) {
      toast.error(t("register.errGender"));
      return;
    }

    if (!formData.terms_accepted) {
      toast.error(t("register.errTerms"));
      return;
    }

    setLoading(true);

    try {
      const saCodePending = localStorage.getItem("pending_sa_code");
      await register({ ...formData, language: lang, sa_code: saCodePending || undefined });
      if (saCodePending) {
        localStorage.removeItem("pending_sa_code");
        toast.success(t("register.saRevealed"), { duration: 9000 });
      }
      toast.success(`Welcome to ${appName}!`);
      navigate("/discover");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("register.errFail"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0">
          <img
            src={isDluxe ? DLUXE_REGISTER_IMG : "https://images.pexels.com/photos/24293777/pexels-photo-24293777.jpeg?auto=compress&cs=tinysrgb&w=1200"}
            alt={appName}
            className={`w-full h-full object-cover ${isDluxe ? "object-top" : ""}`}
            data-testid="register-side-image"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-background via-background/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="glass-card p-6 inline-block">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-6 h-6 text-secondary" />
              <span className="font-semibold">{t("register.newMembers")}</span>
            </div>
            <p className="text-3xl font-bold font-['Outfit'] bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
              {isDluxe ? t("register.promoDluxe1") : t("register.promoLuxe1")}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {isDluxe ? t("register.promoDluxe2") : t("register.promoLuxe2")}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12">
        <div className="max-w-md w-full mx-auto">
          {/* Back button */}
          <div className="flex items-center justify-between mb-8">
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-white -ml-2"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("login.backHome")}
            </Button>
            <LanguageSwitcher />
          </div>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            {isDluxe ? (
              <img src="/logo-dluxe.png" alt="DLuxe-meet.com" className="h-9 w-auto" data-testid="register-dluxe-logo" />
            ) : (
              <>
                <Heart className="w-8 h-8 text-primary fill-primary" />
                <span className="text-2xl font-bold font-['Outfit'] tracking-tight">{appName}</span>
              </>
            )}
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight mb-2">
              {t("register.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("register.subtitle")}
            </p>
          </div>

          {saCode && (
            <div className="mb-6 p-3 rounded-xl bg-pink-500/10 border border-pink-500/30 text-sm text-pink-300" data-testid="sa-banner">
              {t("register.saBanner")}
            </div>
          )}

          {formData.gift_code && (
            <div className="mb-6 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-sm text-amber-300" data-testid="gift-code-banner">
              {giftInfo?.status === "claimed"
                ? t("register.giftClaimed", { code: formData.gift_code })
                : giftInfo?.status === "expired"
                ? t("register.giftExpired", { code: formData.gift_code })
                : t("register.giftReady", { name: giftInfo?.from_name || t("register.giftFriend"), code: formData.gift_code })}
            </div>
          )}

          {/* Google Signup */}
          <Button
            variant="outline"
            className="w-full mb-6 py-6 border-white/10 hover:bg-white/5"
            onClick={handleGoogleSignup}
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("login.google")}
          </Button>
          <p className="text-xs text-muted-foreground text-center -mt-3 mb-6">
            {t("register.googleTerms")}{" "}
            <Link to="/terms" className="text-primary hover:underline" data-testid="google-terms-link">
              {t("register.googleTermsLink")}
            </Link>
          </p>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-background text-muted-foreground">{t("register.orEmail")}</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">{t("register.fullName")}</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder={t("register.namePh")}
                  className="pl-11 h-12 bg-white/5 border-white/10 rounded-xl"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">{t("login.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t("login.emailPh")}
                  className="pl-11 h-12 bg-white/5 border-white/10 rounded-xl"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">{t("login.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  placeholder={t("register.passwordPh")}
                  className="pl-11 pr-11 h-12 bg-white/5 border-white/10 rounded-xl"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">{t("register.iAmA")}</Label>
              <RadioGroup
                value={formData.gender}
                onValueChange={(value) => setFormData({ ...formData, gender: value })}
                className="flex gap-4"
              >
                <div className="flex-1">
                  <RadioGroupItem
                    value="male"
                    id="male"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="male"
                    className="flex items-center justify-center h-12 rounded-xl border border-white/10 bg-white/5 cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/10 transition-colors"
                    data-testid="gender-male"
                  >
                    {t("register.man")}
                  </Label>
                </div>
                <div className="flex-1">
                  <RadioGroupItem
                    value="female"
                    id="female"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="female"
                    className="flex items-center justify-center h-12 rounded-xl border border-white/10 bg-white/5 cursor-pointer peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/10 transition-colors"
                    data-testid="gender-female"
                  >
                    {t("register.woman")}
                  </Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">
                {formData.gender === "female" 
                  ? t("register.hintF") 
                  : formData.gender === "male"
                  ? t("register.hintM")
                  : t("register.hintNone")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="referral" className="text-sm font-medium">
                {t("register.refCode")} <span className="text-muted-foreground font-normal">{t("register.refOptional")}</span>
              </Label>
              <div className="relative">
                <Sparkles className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="referral"
                  type="text"
                  placeholder={t("register.refPh")}
                  className="pl-11 h-12 bg-white/5 border-white/10 rounded-xl uppercase"
                  value={formData.referral_code}
                  onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                  data-testid="referral-code-input"
                />
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => setFormData({ ...formData, terms_accepted: checked === true })}
                className="mt-0.5"
                data-testid="terms-checkbox"
              />
              <Label htmlFor="terms" className="text-sm text-muted-foreground font-normal leading-relaxed cursor-pointer">
                {t("register.terms18")}{" "}
                <Link to="/terms" target="_blank" className="text-primary hover:underline" data-testid="terms-link">
                  {t("register.termsLink")}
                </Link>
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 rounded-xl font-semibold"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                t("register.create")
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-muted-foreground">
            {t("register.haveAccount")}{" "}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
              {t("auth.signIn")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
